import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../App";
import { toast } from "sonner";
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  X,
  Briefcase,
  Star,
  PartyPopper,
  AlertCircle,
  Video,
  Eye,
  Clock,
  ChevronRight,
  Loader2,
  Settings
} from "lucide-react";

const NotificationCenter = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API}/notifications?limit=20`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: "POST",
        credentials: "include",
      });
      
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    setMarkingRead(true);
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: "POST",
        credentials: "include",
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      toast.error("Failed to mark notifications as read");
    } finally {
      setMarkingRead(false);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.notification_id);
    }
    
    setIsOpen(false);
    
    // Navigate based on notification type
    if (notification.type === "application_status" && notification.data?.job_id) {
      navigate(`/my-applications`);
    } else if (notification.type === "interview_scheduled") {
      navigate("/interviews");
    } else if (notification.type === "message") {
      navigate("/messages");
    } else if (notification.data?.link) {
      navigate(notification.data.link);
    }
  };

  const getNotificationIcon = (notification) => {
    const status = notification.data?.new_status || notification.type;
    
    const iconMap = {
      shortlisted: { icon: Star, color: "text-yellow-500", bg: "bg-yellow-500/20" },
      offered: { icon: PartyPopper, color: "text-green-500", bg: "bg-green-500/20" },
      hired: { icon: PartyPopper, color: "text-emerald-500", bg: "bg-emerald-500/20" },
      rejected: { icon: AlertCircle, color: "text-red-500", bg: "bg-red-500/20" },
      reviewed: { icon: Eye, color: "text-blue-500", bg: "bg-blue-500/20" },
      interviewed: { icon: Video, color: "text-purple-500", bg: "bg-purple-500/20" },
      interview_scheduled: { icon: Video, color: "text-purple-500", bg: "bg-purple-500/20" },
      application_status: { icon: Briefcase, color: "text-crimson", bg: "bg-crimson/20" },
      message: { icon: Bell, color: "text-gray-400", bg: "bg-gray-500/20" },
    };
    
    return iconMap[status] || iconMap.application_status;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-white/5 rounded-lg transition-colors"
        data-testid="notification-bell-btn"
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-crimson' : 'text-gray-400'}`} />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-5 h-5 bg-crimson text-white text-xs font-bold rounded-full flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </motion.span>
        )}
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed lg:absolute top-14 lg:top-auto left-4 lg:left-0 right-4 lg:right-auto lg:mt-2 w-auto lg:w-96 bg-obsidian-light border border-[#333] shadow-2xl z-[60] max-h-[70vh] flex flex-col"
            data-testid="notification-dropdown"
          >
            {/* Header */}
            <div className="p-4 border-b border-[#333] flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-white">Notifications</h3>
                {unreadCount > 0 && (
                  <p className="text-xs text-gray-500">{unreadCount} unread</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    disabled={markingRead}
                    className="text-xs text-crimson hover:text-crimson-hover flex items-center gap-1"
                    data-testid="mark-all-read-btn"
                  >
                    {markingRead ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <CheckCheck className="w-3 h-3" />
                    )}
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/notifications/settings");
                  }}
                  className="p-1 hover:bg-white/5 rounded"
                  data-testid="notification-settings-btn"
                >
                  <Settings className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Notification List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="w-6 h-6 animate-spin text-crimson" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <BellOff className="w-12 h-12 text-gray-600 mb-3" />
                  <p className="text-gray-500 text-sm">No notifications yet</p>
                  <p className="text-gray-600 text-xs mt-1">
                    We'll notify you when there's activity on your applications
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-[#222]">
                  {notifications.map((notification) => {
                    const iconConfig = getNotificationIcon(notification);
                    const Icon = iconConfig.icon;
                    
                    return (
                      <motion.button
                        key={notification.notification_id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`w-full p-4 text-left hover:bg-white/5 transition-colors flex gap-3 ${
                          !notification.read ? 'bg-crimson/5' : ''
                        }`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        data-testid={`notification-${notification.notification_id}`}
                      >
                        <div className={`w-10 h-10 rounded-full ${iconConfig.bg} flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-5 h-5 ${iconConfig.color}`} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm ${!notification.read ? 'text-white font-medium' : 'text-gray-300'}`}>
                              {notification.title}
                            </p>
                            {!notification.read && (
                              <span className="w-2 h-2 bg-crimson rounded-full flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          
                          {notification.data?.note && (
                            <p className="text-xs text-gray-600 mt-1 italic line-clamp-1">
                              "{notification.data.note}"
                            </p>
                          )}
                          
                          <div className="flex items-center gap-2 mt-1">
                            <Clock className="w-3 h-3 text-gray-600" />
                            <span className="text-xs text-gray-600">
                              {formatTime(notification.created_at)}
                            </span>
                          </div>
                        </div>
                        
                        <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0 self-center" />
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-[#333]">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/notifications/all");
                  }}
                  className="w-full text-center text-sm text-crimson hover:text-crimson-hover"
                  data-testid="view-all-notifications-btn"
                >
                  View all notifications
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationCenter;
